'use client';

import { environment } from '@/lib/utils/utils';
import { PropsWithChildren, ReactElement, useContext, useEffect } from 'react';
import { I18nDefaultProvider, I18nDefaultContext, I18nContextObj } from '@cvsdigital_web/react-cmk-i18n';
import { translations } from '@/lib/i18n/translations';
import { BehaviorSubject } from 'rxjs';

if (typeof window !== 'undefined') {
  window.document.documentElement.setAttribute('lang', `${window.location.host.includes('es') ? 'es' : 'en'}-US`);
}
const i18nContext = new I18nContextObj(undefined, new BehaviorSubject(environment.experienceApiQee));

/**
 * Internal component that initializes translations after provider is mounted.
 * This pattern ensures setCommon() is called within the provider context.
 */
function I18nInitializer({ children }: PropsWithChildren): ReactElement {
  const context = useContext(I18nDefaultContext);

  useEffect(() => {
    if (context?.i18n) {
      context.i18n.setCommon(translations);
    }
  }, [context]);

  return <>{children}</>;
}

/**
 * I18n Provider wrapper for the application.
 * Wraps children with I18nDefaultProvider and initializes translations.
 *
 * Usage:
 * ```tsx
 * <I18nProvider>
 *   <App />
 * </I18nProvider>
 * ```
 */
export function I18nProvider({ children }: PropsWithChildren): ReactElement {
  return (
    <I18nDefaultProvider value={i18nContext}>
      <I18nInitializer>{children}</I18nInitializer>
    </I18nDefaultProvider>
  );
}

export default I18nProvider;
